import {
  DetailedIconLegalProtections,
  DetailedIconNewConstruction,
  DetailedIconPiggybank,
  Flex,
  Heading,
  MediaObject,
  Paragraph,
  Spacer,
} from '@zillow/constellation';

import {
  StyledSpacerUpsellInfoWrapper,
  StyledTextFinePrint,
  StyledTextUpsellInfo,
  StyledWidgetSpacerMediaObjectWrapper,
} from './StyledComponents';
import { DetailedHomeSparkle } from '../HomeSparkle';

import type { UpsellInfo } from '../../types';

type UpsellWidgetBodyProps = {
  upsellInfo: UpsellInfo;
};

const UpsellWidgetBody = ({ upsellInfo }: UpsellWidgetBodyProps) => {
  const upsellWidgetBodyElements = [
    {
      icon: <DetailedIconPiggybank />,
      text: `Popular plans start at ${upsellInfo.monthlyPriceEstimate}/mo`,
      paragraph: `Join renters near ${upsellInfo.zip}, who protect their personal items from events like theft or damage.`,
    },
    {
      icon: <DetailedHomeSparkle />,
      text: 'Covers furniture, electronics & more',
      paragraph:
        "From clothing to kitchenware, get customizable protection for your stuff whether it's inside your home, on the floor of your car, or strapped to your back on a hike.",
    },
    {
      icon: <DetailedIconLegalProtections />,
      text: 'Protects you financially from injury',
      paragraph:
        "If someone's hurt while in your home, regardless of fault, your policy could help pay the medical expenses and assist with legal expenses.",
    },
    {
      icon: <DetailedIconNewConstruction />,
      text: 'Assists with temporary living costs',
      paragraph:
        "Should your home become unlivable, we'll help cover the additional cost of any temporary living arrangements your are forced to make.",
    },
  ];

  const getSubheadingText = () => {
    if (upsellInfo.isInsuranceRequiredForLease) {
      return 'Your lease requires insurance';
    } else if (upsellInfo.isInsuranceRequiredForListing) {
      return 'Your landlord requires insurance';
    }

    return 'Get coverage in minutes';
  };

  return (
    <Spacer data-testid="upsell-modal-widget-upsell-info" paddingBottom="md">
      <Spacer marginX={{ default: 'lg', md_lte: 'sm' }} paddingX={{ default: 'xl', md_lte: 'sm' }}>
        <Flex marginY="md">
          <Heading level="4" fontFamily="serif">
            Renters insurance
          </Heading>
          <Paragraph fontColor="coolGray500" fontType="body">
            {getSubheadingText()}
          </Paragraph>
        </Flex>
        {upsellWidgetBodyElements.map((element, index) => (
          <StyledWidgetSpacerMediaObjectWrapper key={index}>
            <MediaObject media={element.icon} align="start">
              <StyledSpacerUpsellInfoWrapper>
                <StyledTextUpsellInfo>{element.text}</StyledTextUpsellInfo>
                <Paragraph fontColor="coolGray500" fontType="bodySmall">
                  {element.paragraph}
                </Paragraph>
              </StyledSpacerUpsellInfoWrapper>
            </MediaObject>
          </StyledWidgetSpacerMediaObjectWrapper>
        ))}
        <Flex display="flex" justifyContent="center">
          <StyledTextFinePrint fontColor="gray500" fontType="finePrint">
            Coverages are subject to the terms of your policy and applicable limits.
          </StyledTextFinePrint>
        </Flex>
      </Spacer>
    </Spacer>
  );
};

export default UpsellWidgetBody;
